import { Box, Button, Text, useStyleConfig } from '@chakra-ui/react';
import { ArrowUpRight } from '@time-line/components/icons';
import { useHcpLinkTracking } from 'hooks/useHcpLinkTracking';
import { ACCOUNT } from 'lib/constants/routes';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import type { ReactNode } from 'react';
import { shouldSendReferrer } from 'utils/getExternalLinkProps';
import { getTrackingId } from 'utils/getTrackingId';

interface MobileNavItemProps {
  children: ReactNode;
  href?: string;
  name?: string;
  isExternal?: boolean;
  onClick?: () => void;
  showIcon?: boolean;
}

export function MobileNavItem({
  href,
  children,
  name,
  isExternal,
  onClick,
  showIcon,
  ...props
}: MobileNavItemProps) {
  const id = name
    ? getTrackingId(name, { parent: 'header', platform: 'mobile' })
    : undefined;
  const textStyles = useStyleConfig('Text', { size: 'h3' });
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const router = useRouter();
  const trackHcpLinkClick = useHcpLinkTracking(name, { source: 'menu' });

  const externalRel = shouldSendReferrer(href)
    ? 'noopener'
    : 'noopener noreferrer';

  const handleOnClick = () => {
    if (!onClick) return;

    if (name === 'account' && isAuthenticated) {
      return router.push(ACCOUNT);
    }

    return onClick();
  };

  if (href) {
    const isCurrentPage = router.asPath.split(/[?#]/)[0] === href;

    return (
      <Box
        className="mobile-menu_nav-item"
        as={isExternal ? 'a' : NextLink}
        id={id}
        href={href}
        aria-current={isCurrentPage ? 'page' : undefined}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? externalRel : undefined}
        onClick={trackHcpLinkClick}
        _focusVisible={{ boxShadow: 'outline' }}
        display="flex"
        width="100%"
        justifyContent="space-between"
        borderTopWidth={1}
        borderColor="blackAlpha.250"
        py="xs"
      >
        <Text size="h3" textTransform="none" {...props}>
          {children}
        </Text>
        {showIcon ? <ArrowUpRight boxSize="m" /> : null}
      </Box>
    );
  }

  if (onClick) {
    return (
      <Button
        className="mobile-menu_nav-item"
        variant="unstyled"
        onClick={handleOnClick}
        textAlign="left"
        textTransform="none"
        py="xs"
        display="block"
        borderTopWidth={1}
        borderColor="blackAlpha.250"
        __css={textStyles}
      >
        {children}
      </Button>
    );
  }

  return null;
}
